import * as THREE from 'three';
import { createToonGradientMap, toonMat } from './materials.js';

export const NPC_QUEST_X = 39;
export const NPC_QUEST_Z = 12;

const NEAR_RADIUS = 4.5;
const NEAR_RADIUS_SQ = NEAR_RADIUS * NEAR_RADIUS;
const INTERACT_RANGE = 4.2;
const LOOK_DOT_MIN = 0.78;
const QUEST_TARGET = 2;
const QUEST_MAT_REWARD = 60;
const QUEST_SHIELD_REWARD = 35;

export type NpcQuestState = 'available' | 'accepted' | 'completed';

/** Quest giver NPC at Pleasant Point — one elimination quest per match. */
export class FpsNpcQuest {
  private readonly group: THREE.Group;
  private readonly markerSprite: THREE.Sprite;
  private readonly backdrop: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly bodyEl: HTMLDivElement;
  private readonly actionBtn: HTMLButtonElement;
  private readonly toonGrad: THREE.DataTexture;
  private visible = false;
  private state: NpcQuestState = 'available';
  private progress = 0;
  private bobPhase = Math.random() * Math.PI * 2;

  constructor(
    sceneRoot: THREE.Group,
    mount: HTMLElement,
    private readonly onAccept: () => void,
    private readonly onComplete: () => void,
  ) {
    this.toonGrad = createToonGradientMap();
    this.group = new THREE.Group();
    this.group.position.set(NPC_QUEST_X, 0, NPC_QUEST_Z);
    this.group.rotation.y = -Math.PI * 0.32;
    this.buildNpcMesh();
    this.markerSprite = this.createQuestMarker();
    this.group.add(this.markerSprite);
    sceneRoot.add(this.group);

    this.backdrop = document.createElement('div');
    this.backdrop.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:95',
      'display:none',
      'align-items:center',
      'justify-content:center',
      'background:rgba(6,10,16,0.58)',
      'backdrop-filter:blur(7px)',
      '-webkit-backdrop-filter:blur(7px)',
      'pointer-events:auto',
    ].join(';');

    this.panel = document.createElement('div');
    this.panel.style.cssText = [
      'min-width:300px',
      'padding:18px 20px 16px',
      'border-radius:14px',
      'background:linear-gradient(165deg,rgba(24,28,42,0.97),rgba(10,14,24,0.98))',
      'border:2px solid rgba(255,220,80,0.62)',
      'box-shadow:0 10px 36px rgba(0,0,0,0.52),inset 0 1px 0 rgba(255,255,255,0.05)',
      'color:#e8f4ff',
      'font:14px system-ui,Segoe UI,sans-serif',
      'pointer-events:auto',
    ].join(';');

    this.titleEl = document.createElement('div');
    this.titleEl.style.cssText =
      'font-weight:700;font-size:15px;letter-spacing:0.05em;margin-bottom:4px;color:#ffd966';
    this.titleEl.textContent = 'Quest Giver';
    this.panel.appendChild(this.titleEl);

    const sub = document.createElement('div');
    sub.textContent = 'Pleasant Point';
    sub.style.cssText = 'opacity:0.72;font-size:12px;margin-bottom:12px';
    this.panel.appendChild(sub);

    this.bodyEl = document.createElement('div');
    this.bodyEl.style.cssText = 'margin-bottom:14px;font-size:13px;line-height:1.45;color:#d8e8ff';
    this.panel.appendChild(this.bodyEl);

    this.actionBtn = document.createElement('button');
    this.actionBtn.type = 'button';
    this.actionBtn.style.cssText = [
      'width:100%',
      'padding:10px 12px',
      'border-radius:10px',
      'border:1px solid rgba(255,220,80,0.45)',
      'background:rgba(28,24,12,0.88)',
      'color:#ffe8a0',
      'font:700 13px system-ui,sans-serif',
      'cursor:pointer',
      'transition:background 0.12s,opacity 0.12s',
    ].join(';');
    this.actionBtn.addEventListener('click', () => this.handleAction());
    this.panel.appendChild(this.actionBtn);

    const foot = document.createElement('div');
    foot.textContent = 'Esc — close';
    foot.style.cssText = 'margin-top:10px;text-align:center;opacity:0.55;font-size:11px';
    this.panel.appendChild(foot);

    this.backdrop.appendChild(this.panel);
    mount.appendChild(this.backdrop);
    this.refreshDialog();
  }

  private buildNpcMesh(): void {
    const skin = toonMat(0xd4a574, this.toonGrad, { emissive: 0x221408, emissiveIntensity: 0.12 });
    const shirt = toonMat(0x3a7bd5, this.toonGrad, { emissive: 0x0a1830, emissiveIntensity: 0.18 });
    const pants = toonMat(0x2a3344, this.toonGrad, { emissive: 0x080c14, emissiveIntensity: 0.1 });

    const body = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.72, 0.32), shirt);
    body.position.y = 1.08;
    body.castShadow = true;
    body.receiveShadow = true;
    this.group.add(body);

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.34, 0.34), skin);
    head.position.y = 1.62;
    head.castShadow = true;
    this.group.add(head);

    const legGeo = new THREE.BoxGeometry(0.16, 0.52, 0.16);
    for (const side of [-1, 1] as const) {
      const leg = new THREE.Mesh(legGeo, pants);
      leg.position.set(side * 0.14, 0.48, 0);
      leg.castShadow = true;
      this.group.add(leg);
    }

    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55, 0.62, 0.08, 12),
      toonMat(0x6a8090, this.toonGrad, { emissive: 0x182028, emissiveIntensity: 0.1 }),
    );
    platform.position.y = 0.04;
    platform.receiveShadow = true;
    this.group.add(platform);
  }

  private createQuestMarker(): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffd700';
    ctx.beginPath();
    ctx.arc(32, 32, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#1a1400';
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.fillStyle = '#1a1400';
    ctx.font = 'bold 40px system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('!', 32, 34);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(0.55, 0.55, 1);
    sprite.position.y = 2.15;
    return sprite;
  }

  resetForMatch(): void {
    this.state = 'available';
    this.progress = 0;
    this.close();
    this.markerSprite.visible = true;
    this.refreshDialog();
  }

  getState(): NpcQuestState {
    return this.state;
  }

  getProgress(): number {
    return this.progress;
  }

  getTarget(): number {
    return QUEST_TARGET;
  }

  getHudText(): string {
    if (this.state === 'accepted') {
      return `Quest: Eliminate ${this.progress}/${QUEST_TARGET}`;
    }
    return '';
  }

  isNear(px: number, pz: number): boolean {
    const dx = px - NPC_QUEST_X;
    const dz = pz - NPC_QUEST_Z;
    return dx * dx + dz * dz <= NEAR_RADIUS_SQ;
  }

  getNearHint(): string {
    if (this.state === 'completed') return '';
    return this.state === 'accepted' ? '[E] Quest progress' : '[E] Quest giver';
  }

  shouldShowPrompt(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (this.state === 'completed') return false;
    const center = new THREE.Vector3(NPC_QUEST_X, 1.2, NPC_QUEST_Z);
    const toNpc = center.clone().sub(playerPos);
    const dist = toNpc.length();
    if (dist >= INTERACT_RANGE || dist < 1e-5) return false;
    const dir = toNpc.multiplyScalar(1 / dist);
    return cameraDir.clone().normalize().dot(dir) > LOOK_DOT_MIN;
  }

  open(): void {
    if (this.state === 'completed') return;
    this.visible = true;
    this.backdrop.style.display = 'flex';
    this.refreshDialog();
  }

  close(): void {
    this.visible = false;
    this.backdrop.style.display = 'none';
  }

  isOpen(): boolean {
    return this.visible;
  }

  tryOpenFromInteract(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (this.state === 'completed') return false;
    if (!this.shouldShowPrompt(playerPos, cameraDir) && !this.isNear(playerPos.x, playerPos.z)) {
      return false;
    }
    this.open();
    return true;
  }

  recordPlayerElimination(): void {
    if (this.state !== 'accepted') return;
    this.progress = Math.min(QUEST_TARGET, this.progress + 1);
    if (this.progress >= QUEST_TARGET) {
      this.state = 'completed';
      this.markerSprite.visible = false;
      this.close();
      this.onComplete();
    }
    this.refreshDialog();
  }

  update(dt: number): void {
    if (this.state === 'completed') return;
    if (!Number.isFinite(dt) || dt <= 0) dt = 0;
    this.bobPhase += dt * 2.2;
    this.markerSprite.position.y = 2.15 + Math.sin(this.bobPhase) * 0.08;
  }

  dispose(): void {
    this.backdrop.remove();
    this.group.removeFromParent();
    this.toonGrad.dispose();
    const markerMat = this.markerSprite.material as THREE.SpriteMaterial;
    markerMat.map?.dispose();
    markerMat.dispose();
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry?.dispose();
        if (o.material instanceof THREE.Material) o.material.dispose();
      }
    });
  }

  private handleAction(): void {
    if (this.state === 'available') {
      this.state = 'accepted';
      this.onAccept();
      this.refreshDialog();
      return;
    }
    if (this.state === 'accepted') {
      this.close();
    }
  }

  private refreshDialog(): void {
    if (this.state === 'available') {
      this.bodyEl.innerHTML =
        '<strong>Eliminate 2 opponents</strong><br/>' +
        `<span style="opacity:0.82">Reward: ${QUEST_MAT_REWARD} mats · ${QUEST_SHIELD_REWARD} shield</span>`;
      this.actionBtn.textContent = 'Accept quest';
      this.actionBtn.disabled = false;
      this.actionBtn.style.opacity = '1';
      this.actionBtn.style.cursor = 'pointer';
      return;
    }
    if (this.state === 'accepted') {
      this.bodyEl.innerHTML =
        '<strong>Eliminate 2 opponents</strong><br/>' +
        `<span style="opacity:0.82">Progress: ${this.progress} / ${QUEST_TARGET} (your elims only)</span>`;
      this.actionBtn.textContent = 'Close';
      this.actionBtn.disabled = false;
      this.actionBtn.style.opacity = '1';
      this.actionBtn.style.cursor = 'pointer';
      return;
    }
    this.bodyEl.textContent = 'Quest complete — thanks for the help!';
    this.actionBtn.textContent = 'Done';
    this.actionBtn.disabled = true;
    this.actionBtn.style.opacity = '0.5';
    this.actionBtn.style.cursor = 'default';
  }
}

export { QUEST_MAT_REWARD, QUEST_SHIELD_REWARD };
